import { useChat } from "@ai-sdk/react";
import { useApolloClient } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithApprovalResponses,
} from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAgentUrl } from "./dev-agent-url";
import { MockChatTransport } from "./mock-chat-transport";
import { findScenario, withViewerName } from "./mock-script";
import type { OnboardingUIMessage, WatchOutcome } from "./onboarding-chat";
import {
  createApolloRoutingWatcher,
  createMockRoutingWatcher,
} from "./routing-watcher";

const ROUTING_POLL_INTERVAL_MS = 3000;
const ELAPSED_TICK_MS = 1000;

export type WatchProgress = {
  attempt: number;
  elapsedMs: number;
  outcome?: WatchOutcome;
  detail?: string;
};

export const useOnboardingChat = (scenarioId: string) => {
  const agentUrl = useAgentUrl();
  const { user, getAccessTokenSilently } = useAuth0();
  // auth0-react returns a fresh function identity each render; depending on it
  // rebuilds the transport, which restarts the conversation.
  const getToken = useRef(getAccessTokenSilently);
  getToken.current = getAccessTokenSilently;

  const transport = useMemo(
    () =>
      agentUrl
        ? new DefaultChatTransport<OnboardingUIMessage>({
            api: `${agentUrl.replace(/\/$/, "")}/chat`,
            // api-imaging-study's `epocusOnboardingCaller` resolves the admin from
            // this header specifically; an `authorization` header reaches the agent
            // as nobody.
            headers: async () => ({
              "x-epocus-on-behalf-of-token": await getToken.current(),
            }),
          })
        : new MockChatTransport(
            withViewerName(findScenario(scenarioId), user?.name),
          ),
    [agentUrl, scenarioId, user?.name],
  );

  const chat = useChat<OnboardingUIMessage>({
    transport,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
  });

  const { sendMessage, status, messages } = chat;

  // Seeding the greeting with `setMessages` instead diverges the SDK's internal
  // state, which then appends a snapshot of the message on every update.
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    void sendMessage({
      role: "user",
      parts: [{ type: "data-open", data: {} }],
    });
  }, [sendMessage]);

  const apolloClient = useApolloClient();
  const watcher = useMemo(
    () =>
      transport instanceof MockChatTransport
        ? createMockRoutingWatcher(transport.watchPlan)
        : createApolloRoutingWatcher(apolloClient),
    [transport, apolloClient],
  );

  const [watchProgress, setWatchProgress] = useState<
    Record<string, WatchProgress>
  >({});
  const checkNowRef = useRef<() => void>();

  const pendingWatch = messages
    .flatMap((message) => message.parts)
    .find(
      (part) =>
        part.type === "data-watch" && !watchProgress[part.id ?? ""]?.outcome,
    );
  const watchId =
    pendingWatch?.type === "data-watch" ? pendingWatch.id : undefined;
  const watchStudyUid =
    pendingWatch?.type === "data-watch"
      ? pendingWatch.data.studyInstanceUid
      : undefined;

  useEffect(() => {
    if (!watchId || !watchStudyUid || status === "streaming") return;

    const controller = new AbortController();
    const startedAt = performance.now();
    let attempt = 0;
    let polling = false;
    let settled = false;

    const record = (progress: WatchProgress) =>
      setWatchProgress((current) => ({ ...current, [watchId]: progress }));

    const poll = async () => {
      if (polling || settled || controller.signal.aborted) return;
      polling = true;
      attempt += 1;
      try {
        const elapsedMs = performance.now() - startedAt;
        const observation = await watcher.poll({
          studyInstanceUid: watchStudyUid,
          attempt,
          elapsedMs,
          signal: controller.signal,
        });
        record({ attempt, elapsedMs, ...observation });
        if (!observation.outcome) return;
        settled = true;
        void sendMessage({
          role: "user",
          parts: [
            {
              type: "data-watch-outcome",
              data: {
                outcome: observation.outcome,
                detail: observation.detail,
              },
            },
          ],
        });
      } catch {
        // A failed poll is not a failed verification; the next tick retries.
      } finally {
        polling = false;
      }
    };

    checkNowRef.current = () => void poll();
    const pollTimer = setInterval(() => void poll(), ROUTING_POLL_INTERVAL_MS);
    const elapsedTimer = setInterval(() => {
      if (settled) return;
      record({ attempt, elapsedMs: performance.now() - startedAt });
    }, ELAPSED_TICK_MS);

    return () => {
      clearInterval(pollTimer);
      clearInterval(elapsedTimer);
      checkNowRef.current = undefined;
      controller.abort();
    };
  }, [watchId, watchStudyUid, status, watcher, sendMessage]);

  const checkRoutingNow = useCallback(() => checkNowRef.current?.(), []);

  const progress = useMemo(
    () =>
      messages
        .flatMap((message) => message.parts)
        .reduce<{ step: number; total: number } | undefined>(
          (latest, part) =>
            part.type === "data-progress" ? part.data : latest,
          undefined,
        ),
    [messages],
  );

  const lastMessage = messages.at(-1);
  const textInput = useMemo(
    () =>
      lastMessage?.role === "assistant"
        ? lastMessage.parts.find((part) => part.type === "data-text-input")
            ?.data
        : undefined,
    [lastMessage],
  );

  return { ...chat, watchProgress, checkRoutingNow, progress, textInput };
};
